import { useState, useEffect, useRef } from 'react';
import LeftArrow from "../assets/images/LeftArrow.png";
import RightArrow from "../assets/images/RightArrow.png";
import api from '../services/api';
import ArticleThumbnail from './ArticleThumbnail';

export const LatestPosts = () => {
    const [instagramPosts, setInstagramPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const slidesContainerRef = useRef(null);

    // Cache time in milliseconds (e.g., 1 hour)
    const CACHE_DURATION = 60 * 60 * 1000;

    useEffect(() => {
        loadInstagramPosts();
    }, []);

    const loadInstagramPosts = async () => {
        try {
            setLoading(true);

            // Check if we have cached posts
            const cachedData = localStorage.getItem('instagramPosts');
            const cachedTime = localStorage.getItem('instagramPostsTimestamp');

            // If we have cached data and it's not expired, use it
            if (cachedData && cachedTime) {
                const parsedData = JSON.parse(cachedData);
                const timestamp = parseInt(cachedTime, 10);
                const now = new Date().getTime();

                // Check if the cache is still valid (not expired)
                if (now - timestamp < CACHE_DURATION && parsedData.length > 0) {
                    console.log('Using cached Instagram posts');
                    setInstagramPosts(parsedData);
                    setLoading(false);
                    return;
                }
            }

            // Cache is expired or doesn't exist, fetch from API
            console.log('Fetching fresh Instagram posts');
            const posts = await api.getInstagramPosts(25);

            // Cache the response
            localStorage.setItem('instagramPosts', JSON.stringify(posts));
            localStorage.setItem('instagramPostsTimestamp', new Date().getTime().toString());

            setInstagramPosts(posts);
            setLoading(false);
        } catch (err) {
            console.error('Error fetching Instagram posts:', err);
            setError('Failed to load latest posts');
            setLoading(false);

            // If API fails, try to use cached data even if expired
            const cachedData = localStorage.getItem('instagramPosts');
            if (cachedData) {
                const parsedData = JSON.parse(cachedData);
                if (parsedData.length > 0) {
                    console.log('API failed, using cached data as fallback');
                    setInstagramPosts(parsedData);
                    setError(null); // Clear error since we have something to show
                }
            }
        }
    };

    // Get maximum number of items that can be displayed based on screen size
    const getVisibleItemsPerPage = () => {
        // Maximum of 5 items per row/page regardless of screen size
        return 5;
    };

    const handlePrevSlide = () => {
        const itemsPerPage = getVisibleItemsPerPage();
        const newIndex = Math.max(0, currentIndex - itemsPerPage);
        setCurrentIndex(newIndex);
    };

    const handleNextSlide = () => {
        const itemsPerPage = getVisibleItemsPerPage();
        const newIndex = currentIndex + itemsPerPage;

        if (newIndex < instagramPosts.length) {
            setCurrentIndex(newIndex);
        } else {
            // Cycle to the beginning
            setCurrentIndex(0);
        }
    };

    // Determine which items to show based on current index
    const getVisibleItems = () => {
        if (instagramPosts.length === 0) return [];

        const itemsPerPage = getVisibleItemsPerPage();
        const startIdx = currentIndex;
        const endIdx = Math.min(startIdx + itemsPerPage, instagramPosts.length);
        return instagramPosts.slice(startIdx, endIdx);
    };

    const visibleItems = getVisibleItems();
    const hasMoreNext = currentIndex + getVisibleItemsPerPage() < instagramPosts.length;
    const hasMorePrev = currentIndex > 0;

    if (loading && instagramPosts.length === 0) {
        return (
            <section className="bg-pacifika px-6 sm:px-10 xl:px-59 py-8 sm:py-10 lg:py-20">
                <h4 className="font-tobias text-honeysuckle text-2xl sm:text-3xl lg:text-4xl">The latest</h4>
                <div className="flex justify-center mt-8">
                    <p className="text-white">Loading latest posts...</p>
                </div>
            </section>
        );
    }

    if (error && instagramPosts.length === 0) {
        return (
            <section className="bg-pacifika px-6 sm:px-10 xl:px-59 py-8 sm:py-10 lg:py-20">
                <h4 className="font-tobias text-honeysuckle text-2xl sm:text-3xl lg:text-4xl">The latest</h4>
                <div className="flex justify-center mt-8">
                    <p className="text-white">{error}</p>
                </div>
            </section>
        );
    }

    return (
        <section className="bg-pacifika px-6 sm:px-10 xl:px-59 py-8 sm:py-10 lg:py-20">
            <h4 className="font-tobias text-honeysuckle text-2xl sm:text-3xl lg:text-4xl mb-6 sm:mb-8">The latest</h4>

            <div className="relative">
                {/* Previous button - positioned at the left */}
                <button
                    className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 sm:-translate-x-6 z-10 bg-pacifika/90 p-2 rounded-full ${!hasMorePrev ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                    onClick={handlePrevSlide}
                    disabled={!hasMorePrev}
                    aria-label="Previous articles"
                >
                    <img src={LeftArrow} alt='Previous' />
                </button>

                {/* Grid of articles */}
                <div
                    ref={slidesContainerRef}
                    className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 lg:gap-7 px-6 sm:px-10 xl:px-59"
                >
                    {visibleItems.map((post) => (
                        <div key={post.id} className="w-full">
                            <ArticleThumbnail article={post} />
                        </div>
                    ))}
                </div>

                {/* Next button - positioned at the right */}
                <button
                    className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 sm:translate-x-6 z-10 bg-pacifika/90 p-2 rounded-full ${!hasMoreNext ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:opacity-80'}`}
                    onClick={handleNextSlide}
                    disabled={!hasMoreNext}
                    aria-label="Next articles"
                >
                    <img src={RightArrow} alt='Next' />
                </button>
            </div>
        </section>
    );
}

export default LatestPosts;